import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Link} from 'react-router-dom'
import {ReviewCardDashboard, ReviewStars} from '../../@components'
import {DashboardReviewsGoogle, ReviewItem, ReviewsOutput} from '../../@types'
import {UseWindowSize} from '../../features/hooks/UseWindowSize'
import {PATH_REVIEWS} from '../../routing/paths'

const SLIDE_GAP = 22
const COLLAPSED_COMMENT_LENGTH = 200
const COLLAPSED_CARD_HEIGHT = 300

type Props = {
  data?: DashboardReviewsGoogle
}

const ReviewsSlider = ({data}: Props) => {
  const {width} = UseWindowSize()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [expandedReviews, setExpandedReviews] = useState<Record<number, boolean>>({})
  const [expandedCardHeights, setExpandedCardHeights] = useState<Record<number, number>>({})
  const cardContentRefs = useRef<Record<number, HTMLDivElement | null>>({})

  const reviewsData = useMemo<ReviewsOutput>(
    () => ({
      status: 200,
      warning: false,
      summary: {
        total_count: data?.ratings_google?.user_ratings_total ?? data?.last_reviews_google?.length ?? 0,
        average_rating: Number(data?.ratings_google?.rating ?? 0),
      },
      filters: {
        rating: 0,
        sort: 'recent',
      },
      data: (data?.last_reviews_google ?? []).map((item, index) => ({
        id: index + 1,
        client_id: item.client_id,
        author_name: item.author_name,
        rating: item.rating,
        comment: item.comment,
        review_date: item.review_date,
        external_id: item.external_id,
        created_at: item.review_date,
        updated_at: item.review_date,
      })),
    }),
    [data],
  )

  const hasReviews = reviewsData.data.length > 0
  const hasReviewSummary = reviewsData.summary.total_count > 0
  const visibleCards = width >= 1200 ? 2 : 1
  const maxIndex = Math.max(0, reviewsData.data.length - visibleCards)

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex))
  }, [maxIndex])

  useEffect(() => {
    const nextHeights: Record<number, number> = {}

    reviewsData.data.forEach(({id}) => {
      const content = cardContentRefs.current[id]
      if (content) {
        nextHeights[id] = content.scrollHeight
      }
    })

    setExpandedCardHeights(nextHeights)
  }, [expandedReviews, reviewsData, width])

  const cardBasis = useMemo(
    () => `calc((100% - ${(visibleCards - 1) * SLIDE_GAP}px) / ${visibleCards})`,
    [visibleCards],
  )

  const translateX = useMemo(
    () => `calc(-${currentIndex} * (${cardBasis} + ${SLIDE_GAP}px))`,
    [cardBasis, currentIndex],
  )

  const goPrev = () => setCurrentIndex((prev) => Math.max(0, prev - 1))
  const goNext = () => setCurrentIndex((prev) => Math.min(maxIndex, prev + 1))
  const toggleReview = (id: number) =>
    setExpandedReviews((prev) => ({...prev, [id]: !prev[id]}))
  const getCollapsedComment = (value: string) =>
    value.length > COLLAPSED_COMMENT_LENGTH
      ? `${value.slice(0, COLLAPSED_COMMENT_LENGTH).trimEnd()}...`
      : value

  return (
    <div className="card bg-light-warning h-lg-100">
      <div className="card-body p-5 p-md-7">
        <div className="row g-8 align-items-center">
          <div className="col-xl-3 col-lg-4">
            <div className="d-flex flex-column align-items-center justify-content-center h-100">
              <div className="fs-2hx fw-bolder text-gray-900 mb-4">
                {hasReviewSummary ? 'EXCELLENT' : 'Aucun avis'}
              </div>
              <div className="mb-5">
                <ReviewStars
                  rating={reviewsData.summary.average_rating}
                  iconClassName="fs-2"
                  wrapperClassName="d-flex align-items-center gap-2"
                />
              </div>
              <div className="text-dark fs-3 fw-normal mb-7">
                Base sur{' '}
                <span className="fw-bolder text-decoration-underline">{reviewsData.summary.total_count} avis</span>
              </div>
              {hasReviewSummary && (
                <div className="d-flex align-items-center gap-3">
                  <div className="text-center">
                    <Link to={PATH_REVIEWS.root} className="btn btn-sm btn-primary me-2 px-6">Voir plus</Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="col-xl-9 col-lg-8">
            <div className="position-relative">
              {hasReviews ? (
                <>
                  <div
                    className={`page-item previous position-absolute top-50 start-0 translate-middle z-index-2 ${currentIndex === 0 ? 'disabled' : ''}`}
                    style={{marginLeft: -14}}
                  >
                    <button
                      type="button"
                      className={`page-link bg-primary border-primary ${currentIndex === 0 ? 'invisible' : ''}`}
                      onClick={goPrev}
                      disabled={currentIndex === 0}
                      aria-label="Previous reviews"
                    >
                      <i className="previous" style={{filter: 'brightness(0) invert(1)'}} />
                    </button>
                  </div>

                  <div
                    className={`page-item next position-absolute top-50 end-0 translate-middle z-index-2 ${currentIndex >= maxIndex ? 'disabled' : ''}`}
                    style={{marginRight: -44}}
                  >
                    <button
                      type="button"
                      className={`page-link bg-primary border-primary ${currentIndex === maxIndex ? 'invisible' : ''}`}
                      onClick={goNext}
                      disabled={currentIndex >= maxIndex}
                      aria-label="Next reviews"
                    >
                      <i className="next" style={{filter: 'brightness(0) invert(1)'}} />
                    </button>
                  </div>

                  <div className="overflow-hidden">
                    <div
                      className="d-flex"
                      style={{
                        gap: SLIDE_GAP,
                        transform: `translateX(${translateX})`,
                        transition: 'transform 650ms cubic-bezier(0.22, 1, 0.36, 1)',
                        willChange: 'transform',
                      }}
                    >
                      {reviewsData.data.map((item: ReviewItem, index) => {
                        const isExpanded = !!expandedReviews[item.id]
                        const isFocused = index >= currentIndex && index < currentIndex + visibleCards

                        return (
                          <div
                            key={item.id}
                            style={{
                              flex: `0 0 ${cardBasis}`,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                transform: `scale(${isFocused ? 1 : 1.2})`,
                                transition: 'transform 650ms cubic-bezier(0.22, 1, 0.36, 1)',
                                transformOrigin: 'center center',
                              }}
                            >
                              <ReviewCardDashboard
                                item={item}
                                collapsedHeight={COLLAPSED_CARD_HEIGHT}
                                expandedHeight={expandedCardHeights[item.id] ?? COLLAPSED_CARD_HEIGHT}
                                collapsedComment={getCollapsedComment(item.comment)}
                                isExpanded={isExpanded}
                                onToggle={() => toggleReview(item.id)}
                                contentRef={(element) => {
                                  cardContentRefs.current[item.id] = element
                                }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="d-flex justify-content-center gap-3 mt-5">
                    {Array.from({length: maxIndex + 1}, (_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setCurrentIndex(index)}
                        aria-label={`Go to review slide ${index + 1}`}
                        style={{
                          width: index === currentIndex ? 26 : 10,
                          height: 10,
                          borderRadius: 999,
                          background: index === currentIndex ? '#009df5' : 'rgba(0,156,243,.2)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          border: 'none',
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="row g-5">
                  {Array.from({length: 2}, (_, index) => (
                    <div key={index} className="col-12 col-md-6">
                      <div
                        className="bg-white border h-100"
                        style={{
                          borderRadius: 18,
                          minHeight: COLLAPSED_CARD_HEIGHT,
                          padding: 24,
                          borderColor: '#ece9e2',
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-6">
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                background: '#f1f1f4',
                                flexShrink: 0,
                                marginRight: 16,
                              }}
                            />
                            <div className="w-100">
                              <div
                                style={{
                                  width: 180,
                                  maxWidth: '100%',
                                  height: 16,
                                  borderRadius: 999,
                                  background: '#f1f1f4',
                                  marginBottom: 10,
                                }}
                              />
                              <div
                                style={{
                                  width: 110,
                                  maxWidth: '70%',
                                  height: 12,
                                  borderRadius: 999,
                                  background: '#f1f1f4',
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 my-4">
                          {Array.from({length: 5}, (_, starIndex) => (
                            <div
                              key={starIndex}
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: 6,
                                background: '#f1f1f4',
                              }}
                            />
                          ))}
                        </div>

                        <div className="mb-8">
                          <div
                            style={{
                              width: '100%',
                              height: 14,
                              borderRadius: 999,
                              background: '#f1f1f4',
                              marginBottom: 12,
                            }}
                          />
                          <div
                            style={{
                              width: '92%',
                              height: 14,
                              borderRadius: 999,
                              background: '#f1f1f4',
                              marginBottom: 12,
                            }}
                          />
                          <div
                            style={{
                              width: '84%',
                              height: 14,
                              borderRadius: 999,
                              background: '#f1f1f4',
                            }}
                          />
                        </div>

                        <div
                          style={{
                            width: 95,
                            height: 14,
                            borderRadius: 999,
                            background: '#f1f1f4',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {ReviewsSlider}
